import { useParams } from "react-router";
import useGoBack from "../../../hook/useGoBack";
import { useTranslation } from "react-i18next";
import type { Playlist } from "../../../model/launchpad";
import LaunchpadLayout from "../../launchpad/LaunchpadLayout";
import SongTile from "../../library/SongTile";
import { useSongPlatforms } from "../../library/SongPlatformsProvider";
import useDocumentTitle from "../../../hook/useDocumentTitle";
import { SAMPLE_LAUNCHPAD } from "../../../sample/launchpad";

// Web port of PlaylistDetailView
// (LibraryFeature/View/Screen/PlaylistDetailView.swift).
//
// Its own route rather than a sheet, so a playlist can be linked to directly.
// Songs come from the cached sample Launchpad until the API is wired up.

/**
 * Sized so two columns always fit the narrowest supported phone, then the grid
 * adds columns as width allows. Tiles flex to fill their column rather than
 * being pinned, matching `.adaptive(minimum:)` with `fixedWidth: nil`.
 */
const MINIMUM_TILE_WIDTH = 150;

export default function PlaylistDetail() {
  const { playlistId } = useParams<{ playlistId: string }>();

  const playlist = SAMPLE_LAUNCHPAD.playlists.find((p) => p.id === playlistId);

  return playlist
    ? <FoundPlaylist playlist={playlist} />
    : <MissingPlaylist />
}

function FoundPlaylist(props: { playlist: Playlist }) {
  const { t } = useTranslation("playlistDetail");
  const { t: tLibrary } = useTranslation("library");
  const presentPlatforms = useSongPlatforms();

  const { playlist } = props;
  const songCount = tLibrary("songCount", { count: playlist.songs.length });
  useDocumentTitle(playlist.attributes.name);

  return <LaunchpadLayout>
    <DetailHeader title={playlist.attributes.name} subtitle={songCount} />

    {playlist.songs.length > 0
      ? <div
          className="grid gap-4 px-4 pb-4"
          style={{
            gridTemplateColumns: `repeat(auto-fill, minmax(${MINIMUM_TILE_WIDTH}px, 1fr))`,
          }}
        >
          {playlist.songs.map((song) => (
            <SongTile
              key={song.id}
              title={song.attributes.title}
              artistName={song.attributes.artist.name}
              artworkUrl={song.attributes.artworkURL}
              // fixedWidth: nil — tiles flex to fill their column.
              width={null}
              onClick={() => presentPlatforms?.(song)}
            />
          ))}
        </div>
      : <EmptyState
          title={t("noSongsTitle")}
          description={t("noSongsDescription")}
        />}
  </LaunchpadLayout>
}

function MissingPlaylist() {
  const { t } = useTranslation("playlistDetail");
  useDocumentTitle(t("notFoundTitle"));

  return <LaunchpadLayout>
    <DetailHeader title={t("notFoundTitle")} subtitle={null} />
    <EmptyState
      title={t("notFoundTitle")}
      description={t("notFoundDescription")}
    />
  </LaunchpadLayout>
}

// ToolbarItem(placement: .principal) with a back button, shown inline.
function DetailHeader(props: { title: string, subtitle: string | null }) {
  const { t } = useTranslation("playlistDetail");
  const goBack = useGoBack();

  return <header className="relative flex min-h-11 items-center px-4">
    <button
      type="button"
      onClick={goBack}
      aria-label={t("back")}
      className="absolute left-4 z-10 flex size-9 items-center justify-center rounded-full bg-gray-100 text-gray-800 transition-opacity duration-200 hover:opacity-75"
    >
      <ChevronBackIcon />
    </button>

    {/* Centred on the header regardless of the back button's width. */}
    <div className="mx-auto flex min-w-0 flex-col items-center px-12 text-center">
      <h1 className="max-w-full truncate font-medium text-gray-900">{props.title}</h1>
      {props.subtitle && (
        <p className="max-w-full truncate text-gray-500">{props.subtitle}</p>
      )}
    </div>
  </header>
}

// ContentUnavailableView(..., systemImage: "music.note.slash", ...)
function EmptyState(props: { title: string, description: string }) {
  return <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
    <MusicNoteSlashIcon />
    <p className="text-[17px] font-semibold text-gray-900">{props.title}</p>
    <p className="max-w-xs text-[15px] text-gray-500">{props.description}</p>
  </div>
}

function ChevronBackIcon() {
  return <svg viewBox="0 0 16 16" aria-hidden="true" className="size-4">
    <path d="M10.5 2.5 5 8l5.5 5.5" fill="none" stroke="currentColor" strokeWidth="2.2"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
}

/** SF Symbol: music.note.slash */
function MusicNoteSlashIcon() {
  return <svg viewBox="0 0 32 32" aria-hidden="true" className="size-10 text-gray-400">
    <path fill="currentColor"
      d="M24 4.4a1 1 0 0 0-1.2-1L13.6 5.3a1 1 0 0 0-.8 1v10.4l3-3V8.1l6.2-1.3v6.5a4 4 0 1 0 2 3.5V4.4Z" />
    <path fill="currentColor" d="M12.8 19.5a4 4 0 1 0 3 3.6v-6.6l-3 3Z" />
    <path d="M4 28 28 4" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
  </svg>
}
